import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_ATTEMPTS = 6;
const INTERVAL = 2000;

export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { checkAuth } = useAuth();
  const [state, setState] = useState({ status: "polling", plan: null });

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) {
        setState({ status: "error" });
        return;
      }
      if (attempt >= MAX_ATTEMPTS) {
        setState({ status: "timeout" });
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "success", plan: data.plan_name });
          checkAuth();
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        setTimeout(() => poll(attempt + 1), INTERVAL);
      } catch {
        setState({ status: "error" });
      }
    },
    [sessionId, checkAuth]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  const VIEWS = {
    polling: { icon: <Loader2 className="h-14 w-14 animate-spin text-arjuna-teal" />, title: "Confirming your payment…", body: "Hang tight while we verify the transaction with Stripe." },
    success: { icon: <CheckCircle2 className="h-14 w-14 text-arjuna-agri" />, title: "Payment successful", body: `Your ${state.plan || ""} plan is now active. Welcome aboard.` },
    expired: { icon: <Clock className="h-14 w-14 text-amber-400" />, title: "Session expired", body: "Your checkout session expired. Please try again from Pricing." },
    timeout: { icon: <Clock className="h-14 w-14 text-amber-400" />, title: "Still processing", body: "This is taking longer than usual. Check your email for confirmation." },
    error: { icon: <XCircle className="h-14 w-14 text-rose-500" />, title: "Something went wrong", body: "We couldn't confirm this payment. If you were charged, contact support." },
  };
  const v = VIEWS[state.status] || VIEWS.error;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-arjuna-ink px-6" data-testid="checkout-return">
      <Link to="/" className="mb-12"><Logo className="h-8 w-8" withWordmark /></Link>
      <div className="glass w-full max-w-md rounded-2xl p-10 text-center">
        <div className="flex justify-center">{v.icon}</div>
        <h1 className="mt-6 font-heading text-2xl text-white" data-testid="checkout-status-title">{v.title}</h1>
        <p className="mt-3 text-slate-400">{v.body}</p>
        <div className="mt-8 flex flex-col gap-3">
          {state.status === "success" ? (
            <Link to="/dashboard" className="btn-primary w-full">Go to dashboard</Link>
          ) : (
            <Link to="/#pricing" className="btn-ghost w-full">Back to pricing</Link>
          )}
          <Link to="/" className="text-sm text-slate-400 hover:text-arjuna-teal">Return home</Link>
        </div>
      </div>
    </div>
  );
}
